import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

import type { RelatorioCivelFormData } from '../pages/relatorio-civel-page.models';

import {
  type CalendarioComparecimentoImpressao,
  type RelatorioExportMeta,
  type ResumoRelatorioLinha,
  type SecaoRelatorioExport,
  montarHtmlImpressaoRelatorio,
  montarResumoRelatorioCivel,
  montarSecoesRelatorioCivel,
  totalItensRelatorioCivel,
} from './relatorio-civel-exportacao.utils';

const COLUNAS_PROCESSO = ['Nº do processo', 'Adicionado em'];
const PARES_RESUMO_POR_LINHA = 3;
const COLUNAS_CALENDARIO = ['Semana', 'Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function formatarDataBr(value: Date | string): string {
  const data = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(data.getTime())) return '';
  return data.toLocaleDateString('pt-BR');
}

function linhasProcessos(processos: { numeroProcesso: string; dataAdicao: Date }[]): string[][] {
  return processos.map((processo) => [processo.numeroProcesso, formatarDataBr(processo.dataAdicao)]);
}

function linhasIdentificacaoCompacta(meta: RelatorioExportMeta): string[] {
  const campos = [
    `Defensor: ${meta.defensor}`,
    `Titularidade: ${meta.titularidade}`,
    `Período: ${meta.periodo}`,
    `Defensoria/Vara: ${meta.defensoria}`,
    `Tipo: ${meta.tipo}`,
  ];
  return [campos.slice(0, 3).join('   |   '), campos.slice(3).join('   |   ')];
}

function rotuloResumo(linha: ResumoRelatorioLinha): string {
  return linha.nivel === 1 ? `· ${linha.label}` : linha.label;
}

function linhasResumoGrid(resumo: ResumoRelatorioLinha[]): (string | number)[][] {
  const linhas: (string | number)[][] = [];
  for (let inicio = 0; inicio < resumo.length; inicio += PARES_RESUMO_POR_LINHA) {
    const linha: (string | number)[] = [];
    for (let coluna = 0; coluna < PARES_RESUMO_POR_LINHA; coluna++) {
      const item = resumo[inicio + coluna];
      linha.push(item ? rotuloResumo(item) : '', item ? (item.quantidade ?? '—') : '');
    }
    linhas.push(linha);
  }
  return linhas;
}

function linhasCalendarioComparecimento(calendario: CalendarioComparecimentoImpressao): string[][] {
  return calendario.semanas.map((semana) => [
    semana.nome,
    ...semana.dias.map((dia) => {
      if (!dia) return '';
      const rotulos = [dia.situacao, dia.evento].filter(Boolean);
      const texto = [...new Set(rotulos)].join(' · ');
      return texto ? `${dia.dia} ${texto}` : String(dia.dia);
    }),
  ]);
}

function secoesCriminalExtras(formData: RelatorioCivelFormData): SecaoRelatorioExport[] {
  return [
    {
      aba: '18 Outras Manifest.',
      titulo: '18. Outras Manifestações',
      colunas: COLUNAS_PROCESSO,
      linhas: linhasProcessos(formData.outrasManifestacoes.processos),
    },
    {
      aba: '19 Ciencia',
      titulo: '19. Ciência',
      colunas: COLUNAS_PROCESSO,
      linhas: linhasProcessos(formData.ciencia.processos),
    },
  ];
}

function resumoCriminalExtra(formData: RelatorioCivelFormData): ResumoRelatorioLinha[] {
  return [
    { label: '18. Outras Manifestações', quantidade: formData.outrasManifestacoes.processos.length, nivel: 0 },
    { label: '19. Ciência', quantidade: formData.ciencia.processos.length, nivel: 0 },
  ];
}

export function montarSecoesRelatorioCriminal(formData: RelatorioCivelFormData): SecaoRelatorioExport[] {
  return [...montarSecoesRelatorioCivel(formData), ...secoesCriminalExtras(formData)];
}

export function montarResumoRelatorioCriminal(formData: RelatorioCivelFormData): ResumoRelatorioLinha[] {
  return [...montarResumoRelatorioCivel(formData), ...resumoCriminalExtra(formData)];
}

export function totalItensRelatorioCriminal(formData: RelatorioCivelFormData): number {
  return totalItensRelatorioCivel(formData) + resumoCriminalExtra(formData).reduce((t, l) => t + (l.quantidade ?? 0), 0);
}

export function nomeArquivoRelatorioCriminal(formData: RelatorioCivelFormData): string {
  return `relatorio-atividades-criminal-${formData.ano}-${String(formData.mes).padStart(2, '0')}.xlsx`;
}

/** Mesmas abas do cível + seções 18 (Outras Manifestações) e 19 (Ciência). */
export function exportarRelatorioCriminalExcel(
  formData: RelatorioCivelFormData,
  meta: RelatorioExportMeta,
  calendario: CalendarioComparecimentoImpressao,
): void {
  const workbook = XLSX.utils.book_new();
  const resumo = montarResumoRelatorioCriminal(formData);

  const abaResumo: (string | number)[][] = [
    ['Relatório de Atividades - Criminal'],
    ...linhasIdentificacaoCompacta(meta).map((linha) => [linha]),
    [],
    ...linhasResumoGrid(resumo),
    [],
    ['Total de itens', totalItensRelatorioCriminal(formData)],
  ];
  const planilhaResumo = XLSX.utils.aoa_to_sheet(abaResumo);
  planilhaResumo['!cols'] = Array.from({ length: PARES_RESUMO_POR_LINHA * 2 }, (_, indice) => ({
    wch: indice % 2 === 0 ? 34 : 8,
  }));
  XLSX.utils.book_append_sheet(workbook, planilhaResumo, 'Resumo');

  const abaCalendario: (string | number)[][] = [
    ['1. Comparecimento ao Fórum', meta.periodo],
    ['Dias de presença', calendario.diasPresenca],
    [],
    COLUNAS_CALENDARIO,
    ...linhasCalendarioComparecimento(calendario),
  ];
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(abaCalendario), '01 Comparecimento');

  for (const secao of montarSecoesRelatorioCriminal(formData)) {
    if (secao.linhas.length === 0) continue;
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.aoa_to_sheet([secao.colunas, ...secao.linhas]),
      secao.aba.slice(0, 31),
    );
  }

  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buffer], { type: 'application/octet-stream' }), nomeArquivoRelatorioCriminal(formData));
}

export function montarHtmlImpressaoRelatorioCriminal(
  formData: RelatorioCivelFormData,
  meta: RelatorioExportMeta,
  calendario: CalendarioComparecimentoImpressao,
): string {
  const secoesDetalhadas = montarSecoesRelatorioCriminal(formData).filter(
    (secao) => secao.linhas.length > 0 && !secao.titulo.includes('Comparecimento'),
  );

  return montarHtmlImpressaoRelatorio(
    meta,
    calendario,
    montarResumoRelatorioCriminal(formData),
    secoesDetalhadas,
    totalItensRelatorioCriminal(formData),
  );
}
